import React, { createContext, useState, useEffect, useContext } from 'react';
import { authService } from '../services/authService';
import { profileService } from '../services/profileService';
import { USER_ROLES } from '../constants/authConstants';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    console.log('AuthProvider: Initializing auth listener');
    
    const unsubscribe = authService.initAuthListener();
    
    const unsubscribeAuthState = authService.onAuthStateChange((user) => {
      console.log('AuthProvider: Auth state changed', user ? 'User logged in' : 'User logged out');
      setUser(user);
      setLoading(false);
      setError(null);
    });

    return () => {
      unsubscribe();
      unsubscribeAuthState();
    };
  }, []);

  const signUp = async (email, password, userData) => {
    try {
      setLoading(true);
      setError(null);
      console.log('AuthProvider: Starting sign up process');
      
      const result = await authService.signUp(email, password, userData);
      console.log('AuthProvider: Sign up successful');
      
      return result;
    } catch (error) {
      console.error('AuthProvider: Sign up error', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      console.log('AuthProvider: Starting sign in process');
      
      const result = await authService.signIn(email, password);
      console.log('AuthProvider: Sign in successful');
      
      return result;
    } catch (error) {
      console.error('AuthProvider: Sign in error', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('AuthProvider: Starting sign out process');
      
      await authService.signOut();
      console.log('AuthProvider: Sign out successful');
    } catch (error) {
      console.error('AuthProvider: Sign out error', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email) => {
    try {
      setError(null);
      console.log('AuthProvider: Sending password reset email');
      
      await authService.resetPassword(email);
      console.log('AuthProvider: Password reset email sent');
    } catch (error) {
      console.error('AuthProvider: Password reset error', error);
      setError(error.message);
      throw error;
    }
  };

  const updateUserData = (newUserData) => {
    if (user) {
      const updatedUser = { ...user, ...newUserData };
      setUser(updatedUser);
      console.log('AuthProvider: User data updated locally', updatedUser);
    }
  };

  const updateProfile = async (profileData) => {
    try {
      setError(null);
      console.log('AuthProvider: Updating profile');
      
      if (!user) {
        throw new Error('No authenticated user');
      }

      const updatedData = await profileService.updateProfileInfo(user.uid, profileData);
      
      updateUserData(updatedData);
      
      console.log('AuthProvider: Profile updated successfully');
      return updatedData;
    } catch (error) {
      console.error('AuthProvider: Profile update error', error);
      setError(error.message);
      throw error;
    }
  };

  const uploadProfilePicture = async (file) => {
    try {
      setError(null);
      console.log('AuthProvider: Uploading profile picture');
      
      if (!user) {
        throw new Error('No authenticated user');
      }

      const downloadURL = await profileService.uploadProfilePicture(user.uid, file);
      
      updateUserData({ profilePicture: downloadURL });
      
      console.log('AuthProvider: Profile picture uploaded successfully');
      return downloadURL;
    } catch (error) {
      console.error('AuthProvider: Profile picture upload error', error);
      setError(error.message);
      throw error;
    }
  };

  const removeProfilePicture = async () => {
    try {
      setError(null);
      console.log('AuthProvider: Removing profile picture');
      
      if (!user) {
        throw new Error('No authenticated user');
      }

      await profileService.removeProfilePicture(user.uid);
      
      updateUserData({ profilePicture: null });
      
      console.log('AuthProvider: Profile picture removed successfully');
      return true;
    } catch (error) {
      console.error('AuthProvider: Profile picture removal error', error);
      setError(error.message);
      throw error;
    }
  };

  const getProfileData = async (userId = null) => {
    try {
      setError(null);
      const targetUserId = userId || user?.uid;
      
      if (!targetUserId) {
        throw new Error('No user ID provided');
      }

      console.log('AuthProvider: Getting profile data for', targetUserId);
      const profileData = await profileService.getProfileData(targetUserId);
      
      console.log('AuthProvider: Profile data retrieved');
      return profileData;
    } catch (error) {
      console.error('AuthProvider: Get profile data error', error);
      setError(error.message);
      throw error;
    }
  };

  const refreshUser = async () => {
    try {
      if (!user) return null;

      console.log('AuthProvider: Refreshing user data');
      
      const refreshedAuthUser = await authService.refreshCurrentUser();
      
      console.log('AuthProvider: User refreshed');
      setUser(refreshedAuthUser);
      
      return refreshedAuthUser;
    } catch (error) {
      console.error('AuthProvider: Refresh user error', error);
      return user;
    }
  };

  const clearError = () => {
    setError(null);
  };

  const isAuthenticated = () => {
    return !!user;
  };

  const getUserRole = () => {
    return user?.role || null;
  };

  const getUserDisplayName = () => {
    if (!user) return '';
    return user.fullName || user.email;
  };

  const isAdmin = () => {
    return user?.role === USER_ROLES.ADMIN;
  };

  const isDepartment = () => {
    return user?.role === USER_ROLES.DEPARTMENT;
  };

  const isPlanningOfficial = () => {
    return user?.role === USER_ROLES.PLANNING_OFFICIAL;
  };

  const isDoner = () => {
    return user?.role === USER_ROLES.DONER;
  };

  // Helper: Check if user has admin-like access (Admin, Planning Official or DoNER Official)
  const hasAdminAccess = () => {
    return isAdmin() || isPlanningOfficial() || isDoner();
  };

  // Helper: Check if user can manage users (actions column, add user)
  const canManageUsers = () => {
    return isAdmin(); // Only Admin, not Planning Official
  };

  const hasPermission = (permission) => {
    return authService.hasPermission(permission);
  };

  const canEditProfile = (targetUserId = null) => {
    const userId = targetUserId || user?.uid;
    return profileService.canEditProfile(userId, user);
  };

  const value = {
    user,
    loading,
    error,
    
    signUp,
    signIn,
    signOut,
    resetPassword,
    
    updateUserData,
    updateProfile,
    uploadProfilePicture,
    removeProfilePicture,
    getProfileData,
    refreshUser,
    canEditProfile,
    
    clearError,
    
    isAuthenticated,
    getUserRole,
    getUserDisplayName,
    isAdmin,
    isDepartment,
    isPlanningOfficial,
    isDoner,
    hasAdminAccess,
    canManageUsers,
    hasPermission
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};